import dayjs from "dayjs"
import pluralize from "pluralize"

import { RecurrenceFrequency } from "@constants/choices"

interface FormatRecurrenceParams {
    interval: number
    frequency: number
    startDate: string
    weeklyRecurrence: number[]
    monthlyRecurrence: number
}

/**
 * Converts a dayjs .day() value (0=Sunday) to our Monday-based weekday index (0=Monday).
 */
export function dayjsDayToWeekdayIndex(dayjsDay: number): number {
    return (dayjsDay + 6) % 7
}

// Ordinal position of the date's weekday within its month, e.g. 3 for the third Friday.
function getMonthlyOccurrence(date: dayjs.Dayjs): number {
    return Math.floor((date.date() - date.startOf("month").date()) / 7) + 1
}

// dayjs is only used here for its ordinal suffix ("3rd"); the value is not a date.
function formatOrdinal(value: number): string {
    return dayjs().date(value).format("Do")
}

interface MonthlyRecurrenceOption {
    value: number
    label: string
}

export function getMonthlyRecurrenceOptions(startDate: Date | string | undefined): MonthlyRecurrenceOption[] {
    if (!startDate) {
        return [
            { value: 0, label: "Day of month" },
            { value: 1, label: "Weekday of month" },
        ]
    }

    const date = dayjs(startDate)
    const dayOfMonth = date.format("D")
    const dayOfWeek = date.format("dddd")

    const occurrence = getMonthlyOccurrence(date)
    const occurrenceFormatted = formatOrdinal(occurrence)

    const sameDayLabel = occurrence > 4 ? `The last ${dayOfWeek}` : `The ${occurrenceFormatted} ${dayOfWeek}`

    return [
        { value: 0, label: `Day ${dayOfMonth}` },
        { value: 1, label: sameDayLabel },
    ]
}

/**
 * Short label for a recurrence, e.g. "Monthly", "Quarterly", "Every 2 weeks".
 */
export function formatRecurrenceShorthand(frequency: number, interval: number): string {
    if (frequency === RecurrenceFrequency.DAILY) {
        return interval === 1 ? "Daily" : `Every ${interval} days`
    } else if (frequency === RecurrenceFrequency.WEEKLY) {
        return interval === 1 ? "Weekly" : `Every ${interval} weeks`
    } else if (frequency === RecurrenceFrequency.MONTHLY) {
        if (interval === 1) {
            return "Monthly"
        } else if (interval === 3) {
            return "Quarterly"
        } else if (interval === 6) {
            return "Semiannually"
        } else {
            return `Every ${interval} months`
        }
    } else if (frequency === RecurrenceFrequency.YEARLY) {
        return interval === 1 ? "Yearly" : `Every ${interval} years`
    }

    return "Custom"
}

export default function formatRecurrence(params: FormatRecurrenceParams): string {
    const { interval, frequency, startDate, weeklyRecurrence, monthlyRecurrence } = params

    const date = dayjs(startDate)

    if (frequency === RecurrenceFrequency.DAILY) {
        return `Every ${interval > 1 ? `${interval} ` : ""}${pluralize("Day", interval)}`
    } else if (frequency === RecurrenceFrequency.WEEKLY) {
        const weekdayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
        const weekdays = weeklyRecurrence.map((index) => weekdayNames[index])

        return `Every ${interval > 1 ? `${interval} ` : ""}${pluralize("Week", interval)} on ${weekdays.join(", ")}`
    } else if (frequency === RecurrenceFrequency.MONTHLY) {
        const day = date.format("D")
        const dayOfWeek = date.format("dddd")

        const occurrenceFormatted = formatOrdinal(getMonthlyOccurrence(date))

        if (monthlyRecurrence === 0) {
            return `Every ${interval > 1 ? `${interval} ` : ""}${pluralize("Month", interval)} on day ${day}`
        } else {
            return `Every ${interval > 1 ? `${interval} ` : ""}${pluralize("Month", interval)} on the ${occurrenceFormatted} ${dayOfWeek}`
        }
    } else if (frequency === RecurrenceFrequency.YEARLY) {
        const month = date.format("MMMM")
        const day = date.format("D")
        return `Every ${interval > 1 ? `${interval} ` : ""}${pluralize("Year", interval)} on ${month} ${day}`
    }

    return "Unknown"
}
